import type { MouseEvent } from "react";
import { createElement } from "react";
import { css, styled } from "styled-components";

import {
  BookmarkFilledIcon,
  BookmarkIcon,
  CrossIcon,
  DownloadIcon,
  OpenLinkIcon,
  PinOutlineIcon,
  PinSolidIcon,
  ShareIcon,
  ThumbsDownIcon,
} from "common/components/icons";
import { Spinner } from "common/components/Spinner";
import { useTranslate } from "common/utilities/useTranslate";

type ButtonTypes = "bookmark" | "open" | "pin" | "reject" | "share" | "unpin" | "download" | "cross";

type UIActionButtonProps = {
  hideLabel?: boolean;
  href?: string;
  isActive?: boolean;
  isLoading?: boolean;
  label?: string;
  onClick?: (event: MouseEvent) => void;
  type: ButtonTypes;
};

const IconBackground = styled.div<{ icon: ButtonTypes }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  cursor: pointer;
  transition:
    background-color 0.3s,
    color 0.3s;
  border-radius: 9999px;

  ${({ icon }) => css`
    ${(icon === "unpin" || icon === "download") &&
    css`
      background-color: ${({ theme }) => theme.colors.blueDark};
      color: ${({ theme }) => theme.colors.white};
    `}

    ${icon === "cross" &&
    css`
      background-color: ${({ theme }) => theme.colors.redDark};
      color: ${({ theme }) => theme.colors.white};
    `}
  `}
`;

const StyledLabelSpan = styled.span`
  transition: color 0.3s;
  display: none;
  color: ${({ theme }) => theme.colors.buttonListItemText};

  @media (min-width: 600px) {
    display: inline;
  }
`;

const StyledButton = styled.button<{ isActive: boolean; icon: ButtonTypes }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  padding: 0;
  min-width: 40px;
  min-height: 40px;
  font-size: 1rem;
  cursor: pointer;
  transition:
    background-color 0.3s,
    color 0.3s;
  border: none;
  text-decoration: none;
  white-space: nowrap;
  background-color: unset;
  text-transform: capitalize;
  color: ${({ theme }) => theme.colors.buttonListItemText};

  &:disabled {
    pointer-events: none;
    cursor: not-allowed;
  }

  &:hover,
  &:hover ${StyledLabelSpan} {
    color: ${({ theme }) => theme.colors.buttonNeutralActiveText};
  }

  ${({ icon }) => css`
    ${(icon === "unpin" || icon === "download") &&
    css`
      &:hover ${IconBackground} {
        background-color: ${({ theme }) => theme.colors.blue};
      }
    `}

    ${icon === "cross" &&
    css`
      &:hover ${IconBackground} {
        background-color: ${({ theme }) => theme.colors.red};
      }
    `}
  `}
`;

const StyledSpinner = styled(Spinner)`
  width: 15px;
  height: 15px;
  color: ${({ theme }) => theme.colors.topHeaderBarText};
`;

const getIcon = (type: ButtonTypes, isActive: boolean) => {
  let buttonIcon;
  switch (type) {
    case "bookmark":
      buttonIcon = isActive ? BookmarkFilledIcon : BookmarkIcon;
      break;
    case "open":
      buttonIcon = OpenLinkIcon;
      break;
    case "share":
      buttonIcon = ShareIcon;
      break;
    case "pin":
      buttonIcon = PinOutlineIcon;
      break;
    case "unpin":
      buttonIcon = () => (
        <IconBackground icon={type}>
          <PinSolidIcon width={20} height={20} />
        </IconBackground>
      );
      break;
    case "reject":
      buttonIcon = () => <ThumbsDownIcon width={20} height={20} />;
      break;
    case "download":
      buttonIcon = () => (
        <IconBackground icon={type}>
          <DownloadIcon width={20} height={20} />
        </IconBackground>
      );
      break;
    case "cross":
      buttonIcon = () => (
        <IconBackground icon={type}>
          <CrossIcon width={18} height={18} />
        </IconBackground>
      );
      break;
  }
  return createElement(buttonIcon, {
    style: { width: 20, height: 20 },
  });
};

const getLabelText = (type: string, isActive: boolean) => {
  switch (type) {
    case "bookmark":
      return isActive ? "app.bookmarked.title" : "app.bookmark.title";
    case "open":
      return "app.topic.actions.open";
    case "share":
      return "app.monitor.share";
    case "pin":
      return "app.pinned_knowledge.pin";
    case "unpin":
      return "app.pinned_knowledge.pinned";
    case "reject":
      return "app.reject_content.reject";
    case "download":
      return "app.topic.briefing.download";
    default:
      return undefined;
  }
};

export const UIActionButton = ({
  hideLabel = false,
  href,
  isActive = true,
  isLoading = false,
  label,
  onClick,
  type,
}: UIActionButtonProps) => {
  const translate = useTranslate();
  const defaultLabelText = getLabelText(type, isActive);
  const buttonText = label ?? (defaultLabelText === undefined ? "" : translate(defaultLabelText));

  const buttonOnClick = (event: MouseEvent) => {
    if (onClick) {
      onClick(event);
    }
    if (href) {
      window.open(href, "_blank");
    }
  };

  const onClickHandle = (event: MouseEvent) => {
    event.stopPropagation();
    buttonOnClick(event);
  };

  const getBaseButtonProps = () => {
    if (href) return { as: "a", href, target: "_blank", rel: "noopener noreferrer" };

    return { onClick: onClickHandle };
  };

  return (
    <StyledButton {...getBaseButtonProps()} disabled={isLoading} isActive={isActive} icon={type} title={buttonText}>
      {isLoading ? (
        <div style={{ width: 20, height: 20, display: "flex", alignItems: "center" }}>
          <StyledSpinner />
        </div>
      ) : (
        getIcon(type, isActive)
      )}
      {!hideLabel && <StyledLabelSpan style={{ marginLeft: "10px" }}>{buttonText}</StyledLabelSpan>}
    </StyledButton>
  );
};
